import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { Breakpoint } from '../../hooks/useBreakpoint';
import { bpConfig } from './data';

interface HeroCarouselProps {
  children: React.ReactNode[];
  breakpoint: Breakpoint;
}

const AUTO_INTERVAL = 7000;
const CLICK_PAUSE = 15000;

const tabLabels = ['01 Enroll', '02 Assess', '03 Score', '04 Track'];

const HeroCarousel: React.FC<HeroCarouselProps> = ({ children, breakpoint }) => {
  const [currentCard, setCurrentCard] = useState(0);
  const pausedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cfg = bpConfig[breakpoint];

  const startTimer = useCallback((delay?: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!pausedRef.current) {
        setCurrentCard(prev => (prev + 1) % 4);
      }
      startTimer(AUTO_INTERVAL);
    }, delay || AUTO_INTERVAL);
  }, []);

  useEffect(() => {
    startTimer(AUTO_INTERVAL);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [startTimer]);

  const handleTabClick = (index: number) => {
    setCurrentCard(index);
    startTimer(CLICK_PAUSE);
  };

  return (
    <>
      {/* Carousel area */}
      <div
        className="relative flex-1"
        style={{
          minHeight: cfg.carouselMinH,
          animation: 'fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.6s both',
        }}
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; startTimer(AUTO_INTERVAL); }}
      >
        {children.map((child, i) => (
          <div
            key={i}
            className={`carousel-card ${i === currentCard ? 'active' : ''}`}
          >
            {child}
          </div>
        ))}
      </div>

      {/* Tab buttons */}
      <div
        className="flex items-center gap-2 mt-[14px]"
        style={{ animation: 'fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.7s both' }}
      >
        {tabLabels.map((label, i) => (
          <button
            key={i}
            onClick={() => handleTabClick(i)}
            className={`py-2 px-4 rounded-[6px] text-[10px] tracking-[0.06em] border cursor-pointer transition-all duration-300 ${
              i === currentCard
                ? 'text-white bg-anchor-blue border-anchor-blue'
                : 'text-white/50 bg-[rgba(13,11,45,0.8)] border-white/[0.08] hover:text-white/70 hover:bg-[rgba(13,11,45,0.95)] hover:border-white/[0.15]'
            }`}
            style={{ backdropFilter: 'blur(8px)' }}
          >
            {label}
          </button>
        ))}
      </div>
    </>
  );
};

export default HeroCarousel;
